import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Search, X, Flag, Repeat, Send, CalendarDays, CalendarRange, CalendarClock, Landmark } from "lucide-react";
import PushToggleCard from "@/components/PushToggleCard";
import DaySummaryDialog from "@/components/DaySummaryDialog";
import { Highlight } from "@/lib/highlight";
import { useBankHolidays } from "@/lib/bankHolidays";

const VIEW_MODES = {
    today: { label: "Today", days: 1, icon: CalendarClock },
    week: { label: "Week", days: 7, icon: CalendarRange },
    fortnight: { label: "Fortnight", days: 14, icon: CalendarDays },
};
const VIEW_STORAGE_KEY = "scs.operative.view";

const STATUS_TINTS = {
    scheduled: { bg: "#FFFDF9", border: "#EAE3D3", fg: "#5A4B32" },
    in_progress: { bg: "#FEF3EA", border: "#F4C69C", fg: "#8A4B22" },
    completed: { bg: "#EAF3EE", border: "#C4DACA", fg: "#245B3C" },
    finalised: { bg: "#EEF0FB", border: "#C9CDEE", fg: "#3730A3" },
    cancelled: { bg: "#F3F3F3", border: "#DADADA", fg: "#666" },
};

const startOfDay = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtHM = d => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

function matchesQuery(job, client, q) {
    if (!q) return true;
    const hay = [
        job.title, job.notes, job.address, job.client_name, job.status,
        (job.checklist || []).join(" "),
        client?.address, client?.gate_code, client?.alarm_code,
        client?.key_location, client?.access_notes, client?.parking_notes,
        client?.contact_name, client?.contact_phone, client?.contact_email,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
}

export default function OperativeDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [clientsById, setClientsById] = useState({});
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(VIEW_STORAGE_KEY);
            return VIEW_MODES[saved] ? saved : "today";
        } catch { return "today"; }
    });
    useEffect(() => {
        try { localStorage.setItem(VIEW_STORAGE_KEY, viewMode); } catch { /* ignore */ }
    }, [viewMode]);

    useEffect(() => {
        (async () => {
            try {
                const [j, c] = await Promise.all([
                    api.get("/jobs"),
                    api.get("/clients").catch(() => ({ data: [] })),
                ]);
                setJobs(j.data);
                const map = {};
                for (const cl of c.data) map[cl.id] = cl;
                setClientsById(map);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    // Window whose length depends on the operative's chosen view mode.
    const today = startOfDay(new Date());
    const daysCount = VIEW_MODES[viewMode].days;
    const days = useMemo(
        () => Array.from({ length: daysCount }, (_, i) => addDays(today, i)),
        [today, daysCount]
    );

    const q = query.trim().toLowerCase();
    const filtered = useMemo(
        () => jobs.filter(j => j.status !== "finalised" && matchesQuery(j, clientsById[j.client_id], q)),
        [jobs, clientsById, q]
    );
    const searching = q.length > 0;
    const matchCount = filtered.length;

    const byDay = useMemo(() => {
        const m = new Map();
        for (const j of filtered) {
            const k = startOfDay(new Date(j.scheduled_at)).toDateString();
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(j);
        }
        for (const arr of m.values()) arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        return m;
    }, [filtered]);

    const bankHolidays = useBankHolidays();

    return (
        <div className="space-y-6" data-testid="operative-dashboard">
            <section className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">
                        {viewMode === "today" ? "Today's plan" : viewMode === "week" ? "This week" : "Your fortnight"}
                    </span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="dashboard-heading">
                        Hey {user?.name?.split(" ")[0]}
                    </h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm">
                        {viewMode === "today"
                            ? "Every job you're on today. Tap one to open its checklist and clock in."
                            : `Scroll left → right through the next ${daysCount} days. Tap a job to open its checklist and clock in.`}
                    </p>
                </div>
                <button
                    data-testid="open-summary-btn"
                    onClick={() => setSummaryOpen(true)}
                    className="btn-primary inline-flex items-center gap-2 shrink-0">
                    <Send size={14} /> Send today's summary
                </button>
            </section>

            {/* View toggle: Today / Week / Fortnight */}
            <section
                data-testid="op-view-toggle"
                className="inline-flex rounded-full border border-[var(--border-soft)] bg-white p-1 text-xs font-semibold w-full sm:w-auto overflow-hidden">
                {Object.entries(VIEW_MODES).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    const active = viewMode === key;
                    return (
                        <button
                            key={key}
                            data-testid={`op-view-${key}`}
                            onClick={() => setViewMode(key)}
                            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${
                                active
                                    ? "bg-[var(--pine)] text-white shadow-sm"
                                    : "text-[var(--text-secondary)] hover:text-[var(--pine)]"
                            }`}>
                            <Icon size={13} /> {cfg.label}
                        </button>
                    );
                })}
            </section>

            {/* Search */}
            <section className="relative" data-testid="search-bar">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
                <input
                    data-testid="job-search-input"
                    type="search"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search by street, client, code or checklist…"
                    className="w-full rounded-full pl-10 pr-10 py-3 text-sm bg-white border border-[var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--pine)]/30 focus:border-[var(--pine)]"
                />
                {query && (
                    <button
                        data-testid="clear-search-btn"
                        onClick={() => setQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[var(--text-muted)] hover:text-black hover:bg-[var(--surface-soft)]"
                        aria-label="Clear search">
                        <X size={14} />
                    </button>
                )}
                {searching && (
                    <div className="mt-2 text-xs text-[var(--text-muted)]" data-testid="search-result-count">
                        {matchCount} match{matchCount === 1 ? "" : "es"} for <b className="text-[var(--text-secondary)]">{query}</b>
                    </div>
                )}
            </section>

            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading…</div>
            ) : (
                <div className={viewMode === "today" ? "" : "-mx-4 sm:-mx-6"} data-testid="operative-fortnight-grid">
                    <div className={viewMode === "today"
                        ? ""
                        : "overflow-x-auto px-4 sm:px-6 pb-4 snap-x snap-mandatory"}>
                        <div className={viewMode === "today"
                            ? "flex flex-col gap-3"
                            : "flex gap-3 min-w-max"}>
                            {days.map((d, idx) => {
                                const key = d.toDateString();
                                const isToday = idx === 0;
                                const dayJobs = byDay.get(key) || [];
                                const dayIso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                                const bh = bankHolidays[dayIso];
                                return (
                                    <div
                                        key={key}
                                        data-day-key={key}
                                        data-testid={`op-day-${idx}`}
                                        className={`card-flat p-3 ${viewMode === "today"
                                            ? "w-full min-h-[560px]"
                                            : "w-[240px] shrink-0 snap-start min-h-[560px]"
                                        } ${isToday ? "border-[var(--pine)] ring-1 ring-[var(--pine)]/40 shadow-[0_2px_14px_-6px_rgba(46,125,50,0.35)]" : ""}`}
                                        style={isToday ? { background: "linear-gradient(180deg, rgba(46,125,50,0.09), rgba(46,125,50,0.03))" } : undefined}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">
                                                    {d.toLocaleString(undefined, { weekday: "short" })}
                                                    {isToday && (
                                                        <span
                                                            data-testid="op-today-pill"
                                                            className="ml-1.5 inline-flex items-center rounded-full bg-[var(--pine)] px-1.5 py-[1px] text-[9px] font-bold text-white tracking-wide">
                                                            TODAY
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="font-display text-lg font-semibold">
                                                    {d.getDate()} <span className="text-[10px] text-[var(--text-muted)] uppercase font-normal tracking-wider">
                                                        {d.toLocaleString(undefined, { month: "short" })}
                                                    </span>
                                                </div>
                                            </div>
                                            <span className="text-[10px] text-[var(--text-muted)]">
                                                {dayJobs.length} job{dayJobs.length === 1 ? "" : "s"}
                                            </span>
                                        </div>

                                        {bh && (
                                            <div
                                                data-testid={`op-bank-holiday-${dayIso}`}
                                                className="mb-2 rounded-md bg-[#FFF7E6] border border-[#F4C69C] px-2 py-1 flex items-center gap-1.5"
                                                title={`Irish bank holiday — ${bh}. Repeat jobs skip this day automatically.`}>
                                                <Landmark size={11} className="text-[#8A4B22] shrink-0" />
                                                <span className="text-[10px] font-semibold text-[#8A4B22] uppercase tracking-[0.14em] truncate">
                                                    {bh}
                                                </span>
                                            </div>
                                        )}

                                        <div
                                            className="space-y-2 overflow-y-auto pr-1"
                                            style={{ maxHeight: "460px" }}
                                        >
                                            {dayJobs.length === 0 && (
                                                <div className="text-xs text-[var(--text-muted)] italic py-4 text-center border border-dashed border-[var(--border-soft)] rounded-lg">
                                                    Nothing on
                                                </div>
                                            )}
                                            {dayJobs.map(job => {
                                                const tint = STATUS_TINTS[job.status] || STATUS_TINTS.scheduled;
                                                const active = job.clocked_in_at && !job.clocked_out_at;
                                                return (
                                                    <div
                                                        key={job.id}
                                                        data-testid={`op-job-${job.id}`}
                                                        onClick={() => navigate(`/jobs/${job.id}`)}
                                                        className="rounded-lg p-2.5 cursor-pointer border transition-transform hover:-translate-y-0.5"
                                                        style={{ background: tint.bg, borderColor: tint.border }}
                                                    >
                                                        <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wide" style={{ color: tint.fg }}>
                                                            {fmtHM(new Date(job.scheduled_at))}
                                                            {job.priority === "high" && <Flag size={10} className="text-[var(--terracotta)]" />}
                                                            {job.recurrence_days > 0 && <Repeat size={10} />}
                                                            {active && (
                                                                <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-bold uppercase text-orange-700">
                                                                    <span className="relative flex h-1.5 w-1.5">
                                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-60"></span>
                                                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-500"></span>
                                                                    </span>
                                                                    Live
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-sm font-semibold mt-1 line-clamp-2 leading-tight">
                                                            <Highlight text={job.address || job.title} query={query} />
                                                        </div>
                                                        <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 truncate">
                                                            <Highlight text={job.client_name || "—"} query={query} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <PushToggleCard />

            <DaySummaryDialog open={summaryOpen} onClose={() => setSummaryOpen(false)} />
        </div>
    );
}
